import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Flag, Loader2, CheckCircle2, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatToIST } from "@/utils/dateUtils";

/**
 * Per-LRN Ops Red Flags panel — shows every ACTIVE flag on one order and lets the ops team take
 * the same actions they do on the Ops Red Flags page (POST /red-flags/{id}/action). "Resolved"
 * closes the flag, "False Alarm / Ignore" ignores it, any other action just logs a note/comment.
 * Data (the flags array) is owned by the parent list; after an action we call onActioned() so the
 * parent re-fetches and both the badge count and this list update.
 */

const PRIORITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};
const PRIORITY_RANK = { critical: 3, high: 2, medium: 1, low: 0 };
const PRIORITY_LABELS = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

// Mirrors backend RED_FLAG_ACTIONS — used only if GET /red-flags/actions/available failed to load,
// so the action form is never left empty/unusable. The backend still validates the chosen action.
const FALLBACK_ACTIONS = [
  "Contacted Driver", "Rescheduled Appointment", "Escalated to Manager",
  "Resolved", "False Alarm / Ignore", "Other",
];

/** Highest priority present in a flag list (drives the badge / header colour). */
function maxPriority(flags = []) {
  let best = "low";
  for (const f of flags) {
    if ((PRIORITY_RANK[f.priority] ?? 0) > (PRIORITY_RANK[best] ?? 0)) best = f.priority;
  }
  return best;
}

/**
 * A compact, clickable flag badge for a table row. Renders nothing when the order has no active
 * flags. Colour reflects the highest-priority flag on the order.
 */
export function OrderFlagBadge({ flags = [], onClick }) {
  const count = flags.length;
  if (!count) return null;
  const cls = PRIORITY_STYLES[maxPriority(flags)] || PRIORITY_STYLES.low;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`View ${count} Ops red flag${count > 1 ? "s" : ""} for this LRN`}
      className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${cls} hover:brightness-95`}
    >
      <Flag className="w-3 h-3" />
      {count} flag{count > 1 ? "s" : ""}
    </button>
  );
}

/** One flag card + its action form. Owns only its own draft (action type + notes). */
function FlagCard({ flag, availableActions, canAct, onActioned }) {
  const [actionType, setActionType] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const actions = (availableActions && availableActions.length) ? availableActions : FALLBACK_ACTIONS;

  const pill = PRIORITY_STYLES[flag.priority] || PRIORITY_STYLES.low;
  const logs = Array.isArray(flag.action_logs) ? flag.action_logs : [];
  const mergedReasons = Array.isArray(flag.merged_reasons) ? flag.merged_reasons : [];

  const submit = async () => {
    if (!actionType) { toast.error("Pick an action first"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/red-flags/${flag.id}/action`, {
        action_type: actionType,
        notes: notes.trim() || null,
      });
      const resolving = actionType === "Resolved" || actionType === "False Alarm / Ignore";
      toast.success(resolving ? `Flag ${actionType === "Resolved" ? "resolved" : "ignored"}` : "Note added");
      setActionType("");
      setNotes("");
      onActioned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record action");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${pill}`}>
              {PRIORITY_LABELS[flag.priority] || flag.priority || "—"}
            </span>
            <span className="font-semibold text-slate-800 text-sm">
              {flag.flag_type_name || flag.title}
            </span>
            {flag.is_merged && (
              <span className="inline-flex items-center rounded-full bg-violet-100 text-violet-700 border border-violet-200 px-2 py-0.5 text-[11px] font-semibold">
                Merged
              </span>
            )}
          </div>
          {flag.description && (
            <p className="mt-1 text-sm text-slate-600">{flag.description}</p>
          )}
        </div>
        <span className="shrink-0 text-xs text-slate-400 whitespace-nowrap">
          {flag.created_at ? formatToIST(flag.created_at, true) : ""}
        </span>
      </div>

      {/* All reasons folded into this flag (merge feature) */}
      {mergedReasons.length > 0 && (
        <div className="mt-2 rounded-md bg-slate-50 p-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">All reasons ({mergedReasons.length})</div>
          <ul className="mt-1 space-y-1">
            {mergedReasons.map((r, i) => (
              <li key={i} className="text-xs text-slate-600">
                • <span className="font-medium">{r.flag_type_name || r.title}</span>
                {r.description ? ` — ${r.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Existing comments / action log */}
      {logs.length > 0 && (
        <div className="mt-2 space-y-1">
          {logs.map((l) => (
            <div key={l.id} className="text-xs text-slate-500 border-l-2 border-slate-200 pl-2">
              <span className="font-medium text-slate-600">{l.action_type}</span>
              {l.notes ? <> — {l.notes}</> : null}
              <span className="text-slate-400"> · {l.action_by_name || "—"}{l.action_at ? ` · ${formatToIST(l.action_at, true)}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action form — same actions as the Ops Red Flags page */}
      {canAct ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start">
          <Select value={actionType} onValueChange={setActionType}>
            <SelectTrigger className="sm:w-56">
              <SelectValue placeholder="Take action…" />
            </SelectTrigger>
            <SelectContent>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            rows={1}
            className="flex-1 min-h-[38px]"
          />
          <Button onClick={submit} disabled={submitting || !actionType} className="shrink-0">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" />
              : (actionType === "Resolved" ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <MessageSquarePlus className="w-4 h-4 mr-1" />)}
            {submitting ? "" : (actionType === "Resolved" ? "Resolve" : actionType === "False Alarm / Ignore" ? "Ignore" : "Apply")}
          </Button>
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-400 italic">You don't have permission to act on flags.</div>
      )}
    </div>
  );
}

/**
 * The modal. `flags` is the ACTIVE flag list for `order`, owned by the parent (so it refreshes on
 * action). `availableActions` is the string list from GET /red-flags/actions/available.
 */
export default function OrderRedFlagsDialog({
  open, onOpenChange, order, flags = [], availableActions = [], canAct = false, onActioned,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-red-500" />
            Ops Red Flags ({flags.length})
          </DialogTitle>
          <DialogDescription>
            {order ? `LRN ${order.lrn || "—"} · Order ${order.order_number || order.id}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 space-y-3 py-1">
          {flags.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No active red flags for this LRN.
            </div>
          ) : (
            flags.map((flag) => (
              <FlagCard
                key={flag.id}
                flag={flag}
                availableActions={availableActions}
                canAct={canAct}
                onActioned={onActioned}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
